import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Grid2,
    TextField,
    SelectChangeEvent,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import React, { ChangeEvent, ReactElement, useEffect } from 'react';
import { ClientDto, EntityType } from '../../../../api/ClientApi';
import { EntityTypeSR } from '../../../../common/Types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/reducers';
import {
    createNewEmptyCustomer,
    selectClient,
    toggleCreateNewClient,
    updateClientAddress,
    updateClientCity,
    updateClientContactInfo,
    updateClientEmail,
    updateClientEntityType,
    updateClientName,
    updateClientPhone,
    updateClientShortName,
    updateClientState,
    updateClientTaxId,
    updateClientUcin,
} from '../../../../redux/actions/siteActions';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import ClientAutocomplete from '../../../../common/components/autocomplete/ClientAutocomplete';
import { isEmployeeProjectManager } from '../../../../redux/selectors/employeeSelector';

type ClientFormStepProps = {
    isRequired: boolean;
};

export default function ClientFormStep(props: ClientFormStepProps): ReactElement {
    const { isRequired } = props;

    const client = useAppSelector((state: RootState) => state.site.client);
    const createNewClient = useAppSelector((state: RootState) => state.site.createNewClient);
    const selectedClientId = useAppSelector((state: RootState) => state.site.newClientId);

    const clients = useAppSelector((state: RootState) => state.site.clients);

    const isProjectManager = useAppSelector(isEmployeeProjectManager);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!client) {
            dispatch(createNewEmptyCustomer());
        }
    }, [dispatch]);

    const handleToggleCreateNewClient = () => {
        dispatch(toggleCreateNewClient());
    };

    const handleInputElementChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'ucin':
                dispatch(updateClientUcin(value));
                break;
            case 'phone':
                dispatch(updateClientPhone(value));
                break;
            case 'taxId':
                dispatch(updateClientTaxId(value));
                break;
            case 'address':
                dispatch(updateClientAddress(value));
                break;
            case 'city':
                dispatch(updateClientCity(value));
                break;
            case 'state':
                dispatch(updateClientState(value));
                break;
            case 'contactInfo':
                dispatch(updateClientContactInfo(value));
                break;
            case 'shortName':
                dispatch(updateClientShortName(value));
                break;
            case 'name':
                dispatch(updateClientName(value));
                break;
            case 'email':
                dispatch(updateClientEmail(value));
                break;
            default:
                console.log('%cERROR', 'color: red');
        }
    };

    const handleSelectEntityType = (event: SelectChangeEvent<EntityType>) => {
        const value = event.target.value;
        if (value === EntityType[EntityType.Physical]) {
            dispatch(updateClientEntityType(EntityType.Physical));
        } else if (value === EntityType[EntityType.Legal]) {
            dispatch(updateClientEntityType(EntityType.Legal));
        }
    };

    const handleSelectClient = (clientId?: number) => {
        dispatch(selectClient(clientId));
    };

    return (
        <Box>
            {isProjectManager && (
                <FormControlLabel
                    label="Kreiraj novog klijenta"
                    control={
                        <Checkbox
                            checked={createNewClient}
                            onChange={handleToggleCreateNewClient}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                    }
                />
            )}
            {!createNewClient ? (
                <Box>
                    <div>
                        <ClientAutocomplete
                            isRequired={isRequired}
                            clients={clients ? (clients as ClientDto[]) : []}
                            selectClient={handleSelectClient}
                            selectedClientId={selectedClientId ?? undefined}
                            title="Klijent"
                        />
                    </div>
                </Box>
            ) : (
                <Box>
                    <Grid2 container spacing={2} style={{ maxWidth: '700px', margin: 'auto' }}>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!client?.name || client?.name.trim() === ''}
                                required
                                fullWidth
                                name="name"
                                label="Ime"
                                value={client?.name ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 0, sm: 6 }} style={{ height: 0, margin: 0, padding: 0 }} />
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!client?.shortName || client?.shortName.trim() === ''}
                                required
                                fullWidth
                                name="shortName"
                                label="Skr. ime"
                                value={client?.shortName ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!client?.state || client.state.trim() === ''}
                                required
                                fullWidth
                                name="state"
                                label="Država"
                                value={client?.state ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                name="city"
                                label="Grad"
                                value={client?.city ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                name="address"
                                label="Adresa"
                                value={client?.address ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                name="email"
                                label="Email"
                                value={client?.email ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                name="phone"
                                label="Telefon"
                                value={client?.phone ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!client?.entityType} required>
                                <InputLabel id="demo-simple-select-label">Tip klijenta</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={client?.entityType ?? ''}
                                    label="Tip klijenta"
                                    onChange={handleSelectEntityType}
                                >
                                    {Object.values(EntityType).map((et) => {
                                        return (
                                            <MenuItem key={et} value={et as EntityType}>
                                                {EntityTypeSR[et as EntityType]}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid2>
                        {client?.entityType === EntityType.Legal ? (
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    error={!client?.taxId || client?.taxId.trim() === ''}
                                    required
                                    fullWidth
                                    name="taxId"
                                    label="Jedinstveni broj firme (PIB/JIB...)"
                                    value={client?.taxId ?? ''}
                                    variant="outlined"
                                    onChange={handleInputElementChange}
                                />
                            </Grid2>
                        ) : client?.entityType === EntityType.Physical ? (
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    name="ucin"
                                    label="JMBG"
                                    value={client?.ucin ?? ''}
                                    variant="outlined"
                                    onChange={handleInputElementChange}
                                />
                            </Grid2>
                        ) : (
                            <Grid2 size={{ xs: 12, sm: 6 }} />
                        )}
                    </Grid2>
                </Box>
            )}
        </Box>
    );
}
